import type { SizeUnits } from '../models/viewer-types';

/**
 * Byte count in the largest unit it fits into, binary or decimal.
 *
 * @param {number} bytes The count to format.
 * @param {SizeUnits} [units] Which unit system to use.
 * @return {string} The size with its unit, such as `1.25 MiB`.
 */
export function formatFileSize(bytes: number, units: SizeUnits = 'binary'): string {
  const base = units === 'decimal' ? 1000 : 1024;
  const labels = units === 'decimal' ? ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] : ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];

  let value = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let index = 0;

  while (value >= base && index < labels.length - 1) {
    value /= base;
    index++;
  }

  let rounded = Number(value.toFixed(index ? 2 : 0));

  // Rounding up may carry the value into the next unit: 1023.6 KiB reads as 1024 KiB, which is 1 MiB.
  if (rounded >= base && index < labels.length - 1) {
    rounded = Number((rounded / base).toFixed(2));
    index++;
  }

  return `${rounded} ${labels[index]}`;
}
